import * as Dialog from '@radix-ui/react-dialog';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import {
	Bell, Check, ChevronDown, ChevronRight, CircleHelp, FileText, LayoutDashboard, LogOut, Menu,
	MessageCircle, PackageSearch, Plus, Search, Settings, ShieldCheck, Users, WalletCards, X,
} from 'lucide-react';
import { Link, NavLink, useLocation } from 'react-router';
import type { ReactNode } from 'react';
import { useI18n } from './i18n';
import type { RequestStage } from './domain';
import { useAuth } from './auth';
import { useNavigate } from 'react-router';
import { useAppStore } from './store';

export function VoltMark({ size = 42 }: { size?: number }) {
	return (
		<svg width={size} height={size} viewBox="0 0 48 48" fill="none" aria-label="Вольтик, маскот Вольта" role="img">
			<rect width="48" height="48" rx="15" fill="#D9FF43" />
			<path d="M14.5 20.2c-1.7-1.9-2.5-5.8-.7-7 1.5-1 4.9 2.1 6 3.6m13.7 3.4c1.7-1.9 2.5-5.8.7-7-1.5-1-4.9 2.1-6 3.6" fill="#222621" stroke="#222621" strokeWidth="1.6" strokeLinecap="round" />
			<path d="M14.8 24.1c0-6 4.1-10.2 9.2-10.2s9.2 4.2 9.2 10.2c0 6.3-3.6 10.7-9.2 10.7s-9.2-4.4-9.2-10.7Z" fill="#FFFDF4" stroke="#222621" strokeWidth="1.8" />
			<path d="M20.2 24.5c.9-1.1 2.3-1.1 3.1 0m1.4 0c.9-1.1 2.3-1.1 3.1 0" stroke="#222621" strokeWidth="1.5" strokeLinecap="round" />
			<path d="M22 28.2c1.2.9 2.8.9 4 0" stroke="#222621" strokeWidth="1.5" strokeLinecap="round" />
			<path d="m23.1 26.1.9-.7.9.7-.9.8-.9-.8Z" fill="#222621" />
			<path d="m33.4 8.5-4.2 7.3h3.5l-2.2 6.1 6-8.3h-3.6l.5-5.1Z" fill="#222621" />
		</svg>
	);
}

export function Brand({ compact = false, light = false }: { compact?: boolean; light?: boolean }) {
	const { t } = useI18n();
	return (
		<div className={`brand ${light ? 'brand-light' : ''}`}>
			<VoltMark size={compact ? 36 : 42} />
			<div><strong>ВОЛЬТ</strong>{!compact && <span>{t('brandLine')}</span>}</div>
		</div>
	);
}

export function LocaleSwitcher({ inverse = false }: { inverse?: boolean }) {
	const { locale, setLocale } = useI18n();
	return (
		<div className={`locale-switcher ${inverse ? 'inverse' : ''}`} aria-label={locale === 'ua' ? 'Мова інтерфейсу' : locale === 'en' ? 'Interface language' : 'Язык интерфейса'}>
			{(['ua', 'ru', 'en'] as const).map((item) => (
				<button type="button" key={item} className={locale === item ? 'active' : ''} aria-pressed={locale === item} onClick={() => setLocale(item)}>{item.toUpperCase()}</button>
			))}
		</div>
	);
}

export function Button({ children, variant = 'primary', icon, className = '', ...props }: React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'secondary' | 'ghost' | 'danger'; icon?: ReactNode }) {
	return <button className={`btn btn-${variant} ${className}`} {...props}>{icon}{children}</button>;
}

export function Avatar({ initials, tone = 'lime' }: { initials: string; tone?: 'lime' | 'blue' | 'violet' | 'sand' }) {
	return <span className={`avatar avatar-${tone}`}>{initials}</span>;
}

const stageLabels: Record<'ru' | 'ua' | 'en', Record<RequestStage, string>> = {
	ru: { submitted: 'Новая', clarification_needed: 'Нужно уточнение', sourcing: 'Ищем варианты', offers_published: 'Варианты готовы', client_selected: 'Вариант выбран', purchasing: 'Закупаем', china_warehouse: 'На складе в Китае', international_transit: 'В пути', local_delivery: 'Локальная доставка', ready_for_pickup: 'Готово к выдаче', completed: 'Завершена', cancelled: 'Отменена' },
	ua: { submitted: 'Нова', clarification_needed: 'Потрібне уточнення', sourcing: 'Шукаємо варіанти', offers_published: 'Варіанти готові', client_selected: 'Варіант обрано', purchasing: 'Закуповуємо', china_warehouse: 'На складі в Китаї', international_transit: 'У дорозі', local_delivery: 'Локальна доставка', ready_for_pickup: 'Готово до видачі', completed: 'Завершена', cancelled: 'Скасована' },
	en: { submitted: 'New', clarification_needed: 'Needs clarification', sourcing: 'Sourcing', offers_published: 'Options ready', client_selected: 'Option selected', purchasing: 'Purchasing', china_warehouse: 'At China warehouse', international_transit: 'In transit', local_delivery: 'Local delivery', ready_for_pickup: 'Ready for pickup', completed: 'Completed', cancelled: 'Cancelled' },
};

export function StageBadge({ stage }: { stage: RequestStage }) {
	const { locale } = useI18n();
	return <span className={`status status-${stage}`}><i />{stageLabels[locale][stage]}</span>;
}

interface NavItem { to: string; label: string; icon: ReactNode; badge?: number; }

export function WorkspaceLayout({ children, admin = false }: { children: ReactNode; admin?: boolean }) {
	const { t, locale } = useI18n();
	const { user, profile, logout } = useAuth();
	const { requests, approvals } = useAppStore();
	const navigate = useNavigate();
	const location = useLocation();
	const clientNav: NavItem[] = [
		{ to: '/app', label: t('cabinet'), icon: <LayoutDashboard size={19} /> },
		{ to: '/app/requests', label: t('requests'), icon: <PackageSearch size={19} />, badge: requests.filter((request) => !['completed', 'cancelled'].includes(request.stage)).length },
		{ to: '/app/requests/new', label: t('newRequest'), icon: <Plus size={19} /> },
		{ to: '/app/notifications', label: t('notifications'), icon: <Bell size={19} /> },
	];
	const adminNav: NavItem[] = [
		{ to: '/admin', label: t('queue'), icon: <LayoutDashboard size={19} />, badge: requests.filter((request) => request.attentionReason).length },
		{ to: '/admin/requests', label: t('requests'), icon: <PackageSearch size={19} /> },
		{ to: '/admin/clients', label: t('clients'), icon: <Users size={19} /> },
		{ to: '/admin/team', label: t('team'), icon: <Users size={19} /> },
		{ to: '/admin/roles', label: t('roles'), icon: <ShieldCheck size={19} /> },
		{ to: '/admin/approvals', label: t('approvals'), icon: <Check size={19} />, badge: approvals.filter((approval) => approval.status === 'pending').length },
		{ to: '/admin/settings', label: t('settings'), icon: <Settings size={19} /> },
	];
	const nav = admin ? adminNav : clientNav;
	return (
		<div className="workspace-shell">
			<aside className="sidebar">
				<div className="sidebar-brand"><Brand light /></div>
				<div className="workspace-tag">{admin ? (locale === 'ua' ? 'Команда Вольта' : locale === 'en' ? 'Volt team' : 'Команда Вольта') : profile?.company || user?.name}<ChevronDown size={14} /></div>
				<nav>
					{nav.map((item) => <NavLink key={item.to} to={item.to} end={item.to === '/app' || item.to === '/admin'} className={({ isActive }) => isActive ? 'active' : ''}>{item.icon}<span>{item.label}</span>{item.badge ? <b>{item.badge}</b> : null}</NavLink>)}
				</nav>
				<div className="sidebar-bottom">
					<NavLink to={admin ? '/admin/settings' : '/app/support'}><CircleHelp size={19} /><span>{t('support')}</span></NavLink>
					<div className="sidebar-user"><Avatar initials={(user?.name ?? 'В').split(' ').map((part) => part[0]).join('').slice(0, 2)} tone="sand" /><div><strong>{user?.name}</strong><span>{user?.isSuperAdmin ? (locale === 'ua' ? 'Головний адміністратор' : locale === 'en' ? 'Super administrator' : 'Главный администратор') : user?.email}</span></div><DropdownMenu.Root><DropdownMenu.Trigger asChild><button aria-label={locale === 'ua' ? 'Меню профілю' : locale === 'en' ? 'Profile menu' : 'Меню профиля'}><ChevronDown size={15} /></button></DropdownMenu.Trigger><DropdownMenu.Portal><DropdownMenu.Content className="dropdown-content" sideOffset={8}><DropdownMenu.Item onSelect={() => navigate(admin ? '/admin/profile' : '/app/profile')}>{t('profile')}</DropdownMenu.Item><DropdownMenu.Item onSelect={() => navigate(admin ? '/security/setup' : '/app/security')}>{locale === 'ua' ? 'Безпека' : locale === 'en' ? 'Security' : 'Безопасность'}</DropdownMenu.Item><DropdownMenu.Separator /><DropdownMenu.Item className="danger" onSelect={() => void logout().then(() => navigate('/login'))}><LogOut size={14} /> {t('logout')}</DropdownMenu.Item></DropdownMenu.Content></DropdownMenu.Portal></DropdownMenu.Root></div>
				</div>
			</aside>
			<div className="workspace-main">
				<header className="mobile-workspace-header"><Brand compact /><MobileNav nav={nav} /></header>
				<div className="workspace-topbar"><div /><div className="topbar-actions"><LocaleSwitcher /><Link className="icon-button" aria-label={admin ? 'Апрувы' : 'Уведомления'} to={admin ? '/admin/approvals' : '/app/notifications'}><Bell size={19} /></Link></div></div>
				<main className="workspace-content" key={location.pathname}>{children}</main>
			</div>
		</div>
	);
}

function MobileNav({ nav }: { nav: NavItem[] }) {
	const { locale } = useI18n();
	return <Dialog.Root><Dialog.Trigger asChild><button type="button" className="mobile-menu-button" aria-label={locale === 'ua' ? 'Відкрити меню' : locale === 'en' ? 'Open menu' : 'Открыть меню'}><Menu /></button></Dialog.Trigger><Dialog.Portal><Dialog.Overlay className="dialog-overlay" /><Dialog.Content className="mobile-nav-dialog"><Dialog.Title className="sr-only">{locale === 'ua' ? 'Навігація' : locale === 'en' ? 'Navigation' : 'Навигация'}</Dialog.Title><Dialog.Close className="dialog-close" aria-label={locale === 'ua' ? 'Закрити меню' : locale === 'en' ? 'Close menu' : 'Закрыть меню'}><X /></Dialog.Close><Brand /><nav>{nav.map((item) => <NavLink key={item.to} to={item.to}>{item.icon}{item.label}<ChevronRight size={16} /></NavLink>)}</nav></Dialog.Content></Dialog.Portal></Dialog.Root>;
}

export function PageHeader({ eyebrow, title, description, actions }: { eyebrow?: string; title: string; description?: string; actions?: ReactNode }) {
	return <div className="page-header"><div>{eyebrow && <span className="eyebrow">{eyebrow}</span>}<h1>{title}</h1>{description && <p>{description}</p>}</div>{actions && <div className="page-actions">{actions}</div>}</div>;
}

export function EmptyState({ title, text, action }: { title: string; text: string; action?: ReactNode }) {
	return <div className="empty-state"><div className="mascot-peek"><VoltMark size={70} /></div><h3>{title}</h3><p>{text}</p>{action}</div>;
}

export function Money({ value, currency = 'USD', maximumFractionDigits = 0 }: { value: number; currency?: string; maximumFractionDigits?: number }) {
	return <>{new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits }).format(value)}</>;
}

export function SupportPill() {
	const { t } = useI18n();
	return <a className="support-pill" href="#faq"><MessageCircle size={18} /><span>{t('support')}</span></a>;
}

export const icons = { FileText, WalletCards };
